/**
 * Types for the EP-3 `POST /api/v1/payment-reference/` response (PYMT-2288).
 *
 * The response is a **discriminated union keyed on `type`** — one of `reference`,
 * `redirect`, `qr`, `virtual_account`. The screen renders purely from `type`
 * (plus `code.format`), so Vue no longer carries per-provider knowledge: this
 * replaces the ~40-branch `DynamicProviderLoaderView` switch with four generic,
 * type-driven renderers (redesign spec §2.3 / §4).
 *
 * Field names are camelCased from the snake_case backend payload via
 * `snakeToCamel`. All copy/dates/amounts arrive server-localized and
 * display-ready, so the client renders them verbatim.
 */

/**
 * Closed set of response types (redesign §4). Unknown values must NOT crash the
 * client — the view decodes them to a generic "open in app/website" fallback.
 */
export const V1PaymentReferenceType = {
  Reference: "reference",
  Redirect: "redirect",
  Qr: "qr",
  VirtualAccount: "virtual_account",
} as const;

export type IV1PaymentReferenceType = (typeof V1PaymentReferenceType)[keyof typeof V1PaymentReferenceType];

/**
 * Closed set of `code.format` values (redesign §4). Drives how a code renders:
 * `barcode`/`qr` are drawn locally from `code.value`; `clabe`/`text` show the
 * value as copyable text. Unknown values fall back to plain copyable text.
 */
export const V1CodeFormat = {
  Barcode: "barcode",
  Qr: "qr",
  Clabe: "clabe",
  Text: "text",
} as const;

export type IV1CodeFormat = (typeof V1CodeFormat)[keyof typeof V1CodeFormat];

/** A server-formatted date: `display` is rendered; `value` is the ISO instant. */
export interface IV1ReferenceDate {
  value: string | null;
  display: string | null;
}

/** Provider identity envelope. `logoUrl` is an absolute HTTPS asset. */
export interface IV1ReferenceProvider {
  id: string;
  name: string;
  displayName: string;
  logoUrl: string;
  /** Rail type (same value as the providers endpoint); gate rendering on this, not displayName. */
  type: string;
}

/** Amount: `value` is a fixed-point decimal string; `display` is localized. */
export interface IV1ReferenceAmount {
  value: string;
  /**
   * Localized amount, with any provider fee folded **inline** by the server
   * (e.g. `"$750 + Comisión $17"`); rendered verbatim. There is no separate
   * `fee` field — the client never composes the fee line itself.
   */
  display: string;
}

/** A labeled value row (`bank`, `concept`). Inner fields are required-non-null. */
export interface IV1ReferenceLabeledValue {
  label: string;
  value: string;
}

/** Invoice link. Inner fields are required-non-null when the object is present. */
export interface IV1ReferenceInvoice {
  label: string;
  url: string;
}

/**
 * A note shown under the hero code card (redesign §3b). `level` drives styling:
 * `info` → blue notice, `warn` → warning banner. `title` is optional (warn notes
 * are often body-only). The server sends these as an ordered list (`notes`)
 * so a provider can surface more than one (e.g. Xendit adds a `warn` note).
 */
export interface IV1ReferenceNote {
  level: "info" | "warn";
  title: string | null;
  body: string;
}

/**
 * Leading icon for a hero code card (redesign §3a). `logo` renders the
 * provider's brand asset (barcode/QR store providers); `glyph` renders a blue
 * type-glyph square identifying the rail (`bank` for CLABE, `code` for text).
 */
export type IV1ReferenceCodeIcon =
  | { kind: "logo"; url: string }
  | { kind: "glyph"; name: "bank" | "code" };

/**
 * One payment code (CLABE, barcode digits, QR payload, reference number). A
 * `reference` may carry several (e.g. CLABE + reference number). `value` is the
 * raw payload — for `qr`/`barcode` the client draws it locally, there is no
 * separate image field.
 */
export interface IV1ReferenceCode {
  title?: string;
  label: string;
  value: string;
  display: string;
  /** `IV1CodeFormat`; typed as string for forward-compat (unknown → text). */
  format: string;
  expiration: IV1ReferenceDate | null;
  /**
   * Server-composed sub-line under the code (redesign §3a): the receiving-bank
   * note (`"Transferir a banco NVIO"`) or a `valid-until` line; null when absent.
   */
  caption: string | null;
  /** Hero-card leading icon: brand logo (barcode/QR) or blue type-glyph. */
  icon: IV1ReferenceCodeIcon;
}

/**
 * Common envelope shared by every `type`. `type` is kept as a plain string so
 * unknown server values decode into the fallback instead of failing to parse.
 */
export interface IV1ReferenceCommon {
  type: string;
  title: string;
  provider: IV1ReferenceProvider;
  amount: IV1ReferenceAmount;
  term: string;
  beneficiary: string;
  /** Details-card "Método" value, server-localized (e.g. "Transferencia bancaria"). */
  methodDisplay: string;
  /** Ordered notes rendered under the hero code card (info + optional warn). */
  notes: IV1ReferenceNote[];
  invoice: IV1ReferenceInvoice | null;
}

/** `type: "reference"` — codes + Markdown instructions (OXXO, STP, Paynet, ...). */
export interface IV1ReferenceVariant extends IV1ReferenceCommon {
  type: typeof V1PaymentReferenceType.Reference;
  codes: IV1ReferenceCode[];
  instructions: string;
  bank: IV1ReferenceLabeledValue | null;
  concept: IV1ReferenceLabeledValue | null;
  mapUrl: string | null;
  instructionsImageUrl: string | null;
  instructionsIconUrl: string | null;
}

/** `type: "redirect"` — shares the envelope, carries only `redirectUrl`. */
export interface IV1RedirectVariant extends IV1ReferenceCommon {
  type: typeof V1PaymentReferenceType.Redirect;
  redirectUrl: string;
}

/** `type: "qr"` — a QR drawn from the `format: "qr"` code (PIX, Nequi, CODI). */
export interface IV1QrVariant extends IV1ReferenceCommon {
  type: typeof V1PaymentReferenceType.Qr;
  codes: IV1ReferenceCode[];
  instructions: string | null;
}

/** `type: "virtual_account"` — bank/concept + a CLABE/text code (Indonesia). */
export interface IV1VirtualAccountVariant extends IV1ReferenceCommon {
  type: typeof V1PaymentReferenceType.VirtualAccount;
  codes: IV1ReferenceCode[];
  bank: IV1ReferenceLabeledValue | null;
  concept: IV1ReferenceLabeledValue | null;
  instructions: string | null;
}

/**
 * The full discriminated union. Narrow on `.type` against `V1PaymentReferenceType`;
 * a value outside the union is a forward-compat unknown handled by the fallback.
 */
export type IV1PaymentReferenceResponse =
  | IV1ReferenceVariant
  | IV1RedirectVariant
  | IV1QrVariant
  | IV1VirtualAccountVariant;

/**
 * Request body for `POST /api/v1/payment-reference/`.
 *
 * `providerId` is the provider **name** (routing key, repeats across a grouped
 * family); `sectionId` is the selected leaf/drill-in provider's **id**, which
 * disambiguates families like PAYNET where every store shares `name: "PAYNET"`
 * (GC-496). `days` selects the term; `amount` is the decimal string.
 */
export interface IV1PaymentReferenceRequest {
  providerId: string;
  sectionId?: string;
  amount: string;
  days: number;
}
